import React from 'react';
import { InputAdornment, IconButton, TextField } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { resolveTKey } from '../../../../i18n/resolve-key';

interface TableGlobalSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  showClear?: boolean;
  debounceMs?: number;
  searchableFields?: string[];
}

export function TableGlobalSearch({
  value,
  onChange,
  placeholder,
  disabled,
  showClear = true,
  debounceMs = 300,
}: TableGlobalSearchProps) {
  const { t } = useTranslation();
  const accessibleLabel = placeholder ?? t(resolveTKey('table.globalSearch.placeholder'));
  const [localValue, setLocalValue] = React.useState(value);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  React.useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setLocalValue(nextValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onChange(nextValue), debounceMs);
  };

  const handleClear = () => {
    setLocalValue('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onChange('');
  };

  return (
    <TextField
      size="small"
      variant="outlined"
      fullWidth
      placeholder={accessibleLabel}
      value={localValue}
      onChange={handleChange}
      disabled={disabled}
      sx={{ width: '100%', minWidth: 0, maxWidth: 440 }}
      slotProps={{
        htmlInput: {
          'aria-label': accessibleLabel,
          // Stable semantic hook for the live LFA-011 filtered-empty
          // proof. This is deliberately attached to the actual input, not
          // a wrapper that could remain visible after the control changes.
          'data-testid': 'table-global-search',
        },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" fontSize="small" />
            </InputAdornment>
          ),
          endAdornment:
            showClear && localValue ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear} aria-label={t('common:clear')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
        },
      }}
    />
  );
}
